import { CLIENT_IMAGE_META_DIRECT_TRUST_FIELDS } from '@/ai/image/client-meta';
import { z } from 'zod';
import { adminV2LocalizedTextSchema } from '../translation/translation-contract';

export const adminV2ParameterCalculatorStatuses = [
  'incomplete',
  'ready',
  'warning',
  'no-change',
  'unsupported',
  'invalid',
] as const;

export type AdminV2ParameterCalculatorStatus =
  (typeof adminV2ParameterCalculatorStatuses)[number];

export const adminV2ParameterCalculatorScalarSchema = z.union([
  z.string(),
  z.number().finite(),
  z.boolean(),
]);

export type AdminV2ParameterCalculatorScalar = z.infer<
  typeof adminV2ParameterCalculatorScalarSchema
>;

const userInputSourceSchema = z
  .object({
    kind: z.literal('userInput'),
    control: z.enum(['number', 'select']),
    required: z.boolean(),
    defaultValue: adminV2ParameterCalculatorScalarSchema.optional(),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    step: z.number().positive().finite().optional(),
    options: z
      .array(
        z.object({ value: adminV2ParameterCalculatorScalarSchema }).strict()
      )
      .optional(),
  })
  .strict()
  .superRefine((source, context) => {
    if (
      source.min !== undefined &&
      source.max !== undefined &&
      source.min > source.max
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Calculator user input min cannot exceed max.',
        path: ['min'],
      });
    }
    if (source.control === 'number') {
      if (
        source.defaultValue !== undefined &&
        typeof source.defaultValue !== 'number'
      ) {
        context.addIssue({
          code: 'custom',
          message: 'Calculator number input defaultValue must be numeric.',
          path: ['defaultValue'],
        });
      }
      if (source.options !== undefined) {
        context.addIssue({
          code: 'custom',
          message: 'Calculator number input cannot define options.',
          path: ['options'],
        });
      }
      if (
        typeof source.defaultValue === 'number' &&
        ((source.min !== undefined && source.defaultValue < source.min) ||
          (source.max !== undefined && source.defaultValue > source.max))
      ) {
        context.addIssue({
          code: 'custom',
          message:
            'Calculator number input defaultValue must be within its range.',
          path: ['defaultValue'],
        });
      }
      return;
    }
    if (!source.options?.length) {
      context.addIssue({
        code: 'custom',
        message: 'Calculator select input must define options.',
        path: ['options'],
      });
    }
    if (
      source.min !== undefined ||
      source.max !== undefined ||
      source.step !== undefined
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Calculator select input cannot define numeric constraints.',
        path: ['min'],
      });
    }
    if (
      source.defaultValue !== undefined &&
      !source.options?.some((option) =>
        Object.is(option.value, source.defaultValue)
      )
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Calculator select input defaultValue must match an option.',
        path: ['defaultValue'],
      });
    }
  });

export const adminV2ParameterCalculatorInputSourceSchema = z.discriminatedUnion(
  'kind',
  [
    userInputSourceSchema,
    z
      .object({
        kind: z.literal('toolField'),
        fieldId: z.string().min(1),
      })
      .strict(),
    z
      .object({
        kind: z.literal('mediaMeta'),
        fieldId: z.string().min(1),
        metaField: z.enum(CLIENT_IMAGE_META_DIRECT_TRUST_FIELDS),
      })
      .strict(),
    z
      .object({
        kind: z.literal('fixed'),
        value: adminV2ParameterCalculatorScalarSchema,
      })
      .strict(),
  ]
);

export type AdminV2ParameterCalculatorInputSource = z.infer<
  typeof adminV2ParameterCalculatorInputSourceSchema
>;

export type AdminV2ParameterCalculatorInstance = {
  id: string;
  moduleId: string;
  anchorFieldId: string;
  settings: Record<string, AdminV2ParameterCalculatorScalar>;
  inputBindings: Array<{
    inputId: string;
    source: AdminV2ParameterCalculatorInputSource;
  }>;
  outputMappings: Array<{
    outputId: string;
    targetFieldId: string;
    valueMap?: Array<{
      from: AdminV2ParameterCalculatorScalar;
      to: AdminV2ParameterCalculatorScalar;
    }>;
  }>;
  displayItemIds: string[];
  copy: Array<{
    id: string;
    text: z.infer<typeof adminV2LocalizedTextSchema>;
  }>;
};

const adminV2ParameterCalculatorInstanceObjectSchema = z
  .object({
    id: z.string().min(1),
    moduleId: z.string().min(1),
    anchorFieldId: z.string().min(1),
    settings: z.record(
      z.string().min(1),
      adminV2ParameterCalculatorScalarSchema
    ),
    inputBindings: z.array(
      z
        .object({
          inputId: z.string().min(1),
          source: adminV2ParameterCalculatorInputSourceSchema,
        })
        .strict()
    ),
    outputMappings: z
      .array(
        z
          .object({
            outputId: z.string().min(1),
            targetFieldId: z.string().min(1),
            valueMap: z
              .array(
                z
                  .object({
                    from: adminV2ParameterCalculatorScalarSchema,
                    to: adminV2ParameterCalculatorScalarSchema,
                  })
                  .strict()
              )
              .min(1)
              .optional(),
          })
          .strict()
      )
      .min(1),
    displayItemIds: z.array(z.string().min(1)).min(1),
    copy: z
      .array(
        z
          .object({
            id: z.string().min(1),
            text: adminV2LocalizedTextSchema,
          })
          .strict()
      )
      .min(1),
  })
  .strict();

export const adminV2ParameterCalculatorInstanceSchema: z.ZodType<AdminV2ParameterCalculatorInstance> =
  adminV2ParameterCalculatorInstanceObjectSchema;

export function createAdminV2ParameterCalculatorInstanceSchema(
  localizedTextSchema: z.ZodType<Record<string, string>>
) {
  return adminV2ParameterCalculatorInstanceObjectSchema.extend({
    copy: z
      .array(
        z
          .object({
            id: z.string().min(1),
            text: localizedTextSchema,
          })
          .strict()
      )
      .min(1),
  });
}

export const adminV2ParameterCalculatorInstancesSchema = z
  .array(adminV2ParameterCalculatorInstanceSchema)
  .max(1, 'Phase one supports at most one calculator per Tool.');

export type AdminV2ParameterCalculatorValueType =
  | 'string'
  | 'number'
  | 'boolean';
export type AdminV2ParameterCalculatorInputSourceKind = z.infer<
  typeof adminV2ParameterCalculatorInputSourceSchema
>['kind'];

export type AdminV2ParameterCalculatorDefinition = {
  id: string;
  adminLabel: string;
  description: string;
  settings: Array<{
    id: string;
    label: string;
    type: AdminV2ParameterCalculatorValueType;
    required: boolean;
    defaultValue?: AdminV2ParameterCalculatorScalar;
    min?: number;
    max?: number;
  }>;
  inputs: Array<{
    id: string;
    label: string;
    type: AdminV2ParameterCalculatorValueType;
    required: boolean;
    allowedSources: AdminV2ParameterCalculatorInputSourceKind[];
    allowedValues?: AdminV2ParameterCalculatorScalar[];
  }>;
  outputs: Array<{
    id: string;
    label: string;
    type: AdminV2ParameterCalculatorValueType;
    allowedValues?: AdminV2ParameterCalculatorScalar[];
  }>;
  displayItems: Array<{
    id: string;
    label: string;
    outputId: string;
  }>;
  copyItems: Array<{
    id: string;
    label: string;
    required: boolean;
    defaultText: string;
  }>;
  examples: Array<{
    inputs: Record<string, AdminV2ParameterCalculatorScalar>;
    settings: Record<string, AdminV2ParameterCalculatorScalar>;
    outputs: Record<string, AdminV2ParameterCalculatorScalar>;
  }>;
  calculate: (params: {
    inputs: Record<string, AdminV2ParameterCalculatorScalar>;
    settings: Record<string, AdminV2ParameterCalculatorScalar>;
  }) => AdminV2ParameterCalculatorModuleResult;
};

export type AdminV2ParameterCalculatorModuleResult = {
  status: Exclude<AdminV2ParameterCalculatorStatus, 'no-change'>;
  outputs: Record<string, AdminV2ParameterCalculatorScalar>;
  messageId?: string;
};

export type AdminV2ParameterCalculatorResult = Omit<
  AdminV2ParameterCalculatorModuleResult,
  'status'
> & {
  status: AdminV2ParameterCalculatorStatus;
  displayItems: Array<{
    id: string;
    outputId: string;
    value: AdminV2ParameterCalculatorScalar;
  }>;
};

const sharedCopyItems = [
  {
    id: 'entry.label',
    label: 'Entry label',
    required: true,
    defaultText: 'Parameter calculator',
  },
  {
    id: 'panel.title',
    label: 'Panel title',
    required: true,
    defaultText: 'Recommended parameters',
  },
  {
    id: 'panel.description',
    label: 'Panel description',
    required: false,
    defaultText: '',
  },
] satisfies AdminV2ParameterCalculatorDefinition['copyItems'];

const internalLinearPairModule: AdminV2ParameterCalculatorDefinition = {
  id: 'internal/test-linear-pair-v1',
  adminLabel: 'Internal test: linear pair',
  description: 'Deterministically returns two numeric recommendations.',
  settings: [
    {
      id: 'multiplier',
      label: 'Multiplier',
      type: 'number',
      required: true,
      defaultValue: 2,
      min: 0,
    },
  ],
  inputs: [
    {
      id: 'base',
      label: 'Base value',
      type: 'number',
      required: true,
      allowedSources: ['userInput', 'toolField', 'mediaMeta', 'fixed'],
    },
    {
      id: 'offset',
      label: 'Offset',
      type: 'number',
      required: true,
      allowedSources: ['userInput', 'toolField', 'mediaMeta', 'fixed'],
    },
  ],
  outputs: [
    { id: 'primary', label: 'Primary recommendation', type: 'number' },
    { id: 'secondary', label: 'Secondary recommendation', type: 'number' },
  ],
  displayItems: [
    { id: 'primary', label: 'Primary result', outputId: 'primary' },
    { id: 'secondary', label: 'Secondary result', outputId: 'secondary' },
  ],
  copyItems: [
    ...sharedCopyItems,
    {
      id: 'input.base.label',
      label: 'Base input label',
      required: true,
      defaultText: 'Base',
    },
    {
      id: 'input.base.help',
      label: 'Base input help',
      required: false,
      defaultText: '',
    },
    {
      id: 'input.base.unit',
      label: 'Base input unit',
      required: false,
      defaultText: '',
    },
    {
      id: 'input.offset.label',
      label: 'Offset input label',
      required: true,
      defaultText: 'Offset',
    },
    {
      id: 'input.offset.help',
      label: 'Offset input help',
      required: false,
      defaultText: '',
    },
    {
      id: 'input.offset.unit',
      label: 'Offset input unit',
      required: false,
      defaultText: '',
    },
    {
      id: 'result.primary.label',
      label: 'Primary result label',
      required: true,
      defaultText: 'Primary',
    },
    {
      id: 'result.secondary.label',
      label: 'Secondary result label',
      required: true,
      defaultText: 'Secondary',
    },
    {
      id: 'message.large-result',
      label: 'Large result warning',
      required: true,
      defaultText: 'The recommendation is unusually large.',
    },
  ],
  examples: [
    {
      inputs: { base: 10, offset: 1 },
      settings: { multiplier: 2 },
      outputs: { primary: 11, secondary: 21 },
    },
  ],
  calculate: ({ inputs, settings }) => {
    const base = inputs.base;
    const offset = inputs.offset;
    const multiplier = settings.multiplier;
    if (
      typeof base !== 'number' ||
      typeof offset !== 'number' ||
      typeof multiplier !== 'number'
    ) {
      return { status: 'invalid', outputs: {} };
    }
    if (base < 0) {
      return { status: 'unsupported', outputs: {} };
    }
    const outputs = {
      primary: base + offset,
      secondary: base * multiplier + offset,
    };
    return Math.max(outputs.primary, outputs.secondary) > 1_000
      ? {
          status: 'warning',
          outputs,
          messageId: 'message.large-result',
        }
      : { status: 'ready', outputs };
  },
};

const internalChoiceModule: AdminV2ParameterCalculatorDefinition = {
  id: 'internal/test-choice-v1',
  adminLabel: 'Internal test: choice',
  description:
    'Second deterministic module used to verify registry extensibility.',
  settings: [],
  inputs: [
    {
      id: 'choice',
      label: 'Choice',
      type: 'string',
      required: true,
      allowedSources: ['userInput', 'toolField', 'fixed'],
      allowedValues: ['a', 'b'],
    },
  ],
  outputs: [
    {
      id: 'choice',
      label: 'Recommended choice',
      type: 'string',
      allowedValues: ['a', 'b'],
    },
  ],
  displayItems: [{ id: 'choice', label: 'Choice result', outputId: 'choice' }],
  copyItems: [
    ...sharedCopyItems,
    {
      id: 'input.choice.label',
      label: 'Choice input label',
      required: true,
      defaultText: 'Choice',
    },
    {
      id: 'input.choice.help',
      label: 'Choice input help',
      required: false,
      defaultText: '',
    },
    {
      id: 'input.choice.unit',
      label: 'Choice input unit',
      required: false,
      defaultText: '',
    },
    {
      id: 'result.choice.label',
      label: 'Choice result label',
      required: true,
      defaultText: 'Choice',
    },
    {
      id: 'input.choice.option.a',
      label: 'Choice A label',
      required: true,
      defaultText: 'A',
    },
    {
      id: 'input.choice.option.b',
      label: 'Choice B label',
      required: true,
      defaultText: 'B',
    },
  ],
  examples: [
    {
      inputs: { choice: 'a' },
      settings: {},
      outputs: { choice: 'a' },
    },
  ],
  calculate: ({ inputs }) => {
    if (typeof inputs.choice !== 'string' || inputs.choice.length === 0) {
      return {
        status: 'invalid',
        outputs: {} as Record<string, AdminV2ParameterCalculatorScalar>,
      };
    }
    return { status: 'ready', outputs: { choice: inputs.choice } };
  },
};

const parameterCalculatorDefinitions = [
  internalLinearPairModule,
  internalChoiceModule,
] as const;

const parameterCalculatorDefinitionById = new Map(
  parameterCalculatorDefinitions.map((definition) => [
    definition.id,
    definition,
  ])
);

export function listAdminV2ParameterCalculatorDefinitions() {
  return parameterCalculatorDefinitions;
}

export function getAdminV2ParameterCalculatorDefinition(moduleId: string) {
  return parameterCalculatorDefinitionById.get(moduleId) ?? null;
}

function isValueType(
  value: AdminV2ParameterCalculatorScalar,
  type: AdminV2ParameterCalculatorValueType
) {
  if (type === 'string') return typeof value === 'string';
  if (type === 'number') return typeof value === 'number';
  return typeof value === 'boolean';
}

type CalculatorToolField = {
  id: string;
  control: string;
  inputRole?: string;
  userVisible?: boolean;
  adminOnly?: boolean;
  min?: number;
  max?: number;
  options?: Array<{ value: AdminV2ParameterCalculatorScalar }>;
  clientImageMetaRequirements?: {
    fields: string[];
  };
};

export function getAdminV2ParameterCalculatorToolFieldValueType(
  field: CalculatorToolField
): AdminV2ParameterCalculatorValueType | null {
  if (field.control === 'number' || field.control === 'slider') return 'number';
  if (field.control === 'toggle') return 'boolean';
  if (field.control === 'text' || field.control === 'url') return 'string';
  if (field.control === 'select') {
    const types = new Set(
      (field.options ?? []).map((option) => typeof option.value)
    );
    return types.size === 1 &&
      ['string', 'number', 'boolean'].includes([...types][0] ?? '')
      ? ([...types][0] as AdminV2ParameterCalculatorValueType)
      : null;
  }
  return null;
}

export function isAdminV2ParameterCalculatorVisibleParameterField(
  field: CalculatorToolField | undefined
) {
  return Boolean(
    field &&
      field.userVisible !== false &&
      field.adminOnly !== true &&
      (field.inputRole ?? 'parameter') === 'parameter' &&
      getAdminV2ParameterCalculatorToolFieldValueType(field)
  );
}

export type AdminV2ParameterCalculatorConfigIssue = {
  message: string;
  path: Array<string | number>;
};

export function validateAdminV2ParameterCalculatorInstance(params: {
  instance: Omit<AdminV2ParameterCalculatorInstance, 'copy'> & {
    copy: Array<{ id: string }>;
  };
  toolFields: CalculatorToolField[];
}): AdminV2ParameterCalculatorConfigIssue[] {
  const issues: AdminV2ParameterCalculatorConfigIssue[] = [];
  const add = (message: string, path: Array<string | number>) =>
    issues.push({ message, path });
  const definition = getAdminV2ParameterCalculatorDefinition(
    params.instance.moduleId
  );
  if (!definition) {
    add('Calculator module must be registered in code.', ['moduleId']);
    return issues;
  }

  const fieldById = new Map(
    params.toolFields.map((field) => [field.id, field])
  );
  if (
    !isAdminV2ParameterCalculatorVisibleParameterField(
      fieldById.get(params.instance.anchorFieldId)
    )
  ) {
    add('Calculator anchor must reference a visible parameter Tool field.', [
      'anchorFieldId',
    ]);
  }

  const inputById = new Map(
    definition.inputs.map((input) => [input.id, input])
  );
  const seenInputIds = new Set<string>();
  params.instance.inputBindings.forEach((binding, index) => {
    const input = inputById.get(binding.inputId);
    if (!input) {
      add('Calculator input binding references an unknown module input.', [
        'inputBindings',
        index,
        'inputId',
      ]);
      return;
    }
    if (seenInputIds.has(binding.inputId)) {
      add('Calculator input bindings must be unique.', [
        'inputBindings',
        index,
        'inputId',
      ]);
    }
    seenInputIds.add(binding.inputId);
    if (!input.allowedSources.includes(binding.source.kind)) {
      add('Calculator input source is not allowed by the registered module.', [
        'inputBindings',
        index,
        'source',
      ]);
      return;
    }
    if (binding.source.kind === 'fixed') {
      const fixedValue = binding.source.value;
      if (!isValueType(fixedValue, input.type)) {
        add('Calculator fixed input type does not match the module input.', [
          'inputBindings',
          index,
          'source',
          'value',
        ]);
      }
      if (input.required && fixedValue === '') {
        add('Calculator required fixed input cannot be empty.', [
          'inputBindings',
          index,
          'source',
          'value',
        ]);
      }
      if (
        input.allowedValues &&
        !input.allowedValues.some((value) => Object.is(value, fixedValue))
      ) {
        add('Calculator fixed input is not registered by the module.', [
          'inputBindings',
          index,
          'source',
          'value',
        ]);
      }
      return;
    }
    if (binding.source.kind === 'userInput') {
      const expectedControl = input.type === 'number' ? 'number' : 'select';
      if (binding.source.control !== expectedControl) {
        add(
          'Calculator user input control does not match the module input type.',
          ['inputBindings', index, 'source', 'control']
        );
      }
      if (input.required && !binding.source.required) {
        add('A required module input cannot be optional for the user.', [
          'inputBindings',
          index,
          'source',
          'required',
        ]);
      }
      const seenOptions = new Set<string>();
      for (const option of binding.source.options ?? []) {
        const optionKey = `${typeof option.value}:${String(option.value)}`;
        if (seenOptions.has(optionKey)) {
          add('Calculator user input options must be unique.', [
            'inputBindings',
            index,
            'source',
            'options',
          ]);
          break;
        }
        seenOptions.add(optionKey);
        if (!isValueType(option.value, input.type)) {
          add(
            'Calculator user input option type does not match the module input.',
            ['inputBindings', index, 'source', 'options']
          );
          break;
        }
        if (
          input.allowedValues &&
          !input.allowedValues.some((value) => Object.is(value, option.value))
        ) {
          add('Calculator user input option is not registered by the module.', [
            'inputBindings',
            index,
            'source',
            'options',
          ]);
          break;
        }
      }
      return;
    }
    const sourceField = fieldById.get(binding.source.fieldId);
    if (!sourceField) {
      add('Calculator input references an unknown Tool field.', [
        'inputBindings',
        index,
        'source',
        'fieldId',
      ]);
      return;
    }
    if (binding.source.kind === 'toolField') {
      if (!isAdminV2ParameterCalculatorVisibleParameterField(sourceField)) {
        add(
          'Calculator Tool field input must reference a visible parameter field.',
          ['inputBindings', index, 'source', 'fieldId']
        );
      } else if (
        getAdminV2ParameterCalculatorToolFieldValueType(sourceField) !==
        input.type
      ) {
        add(
          'Calculator Tool field input type does not match the module input.',
          ['inputBindings', index, 'source', 'fieldId']
        );
      }
      if (
        input.allowedValues &&
        sourceField.options?.some(
          (option) =>
            !input.allowedValues?.some((value) =>
              Object.is(value, option.value)
            )
        )
      ) {
        add('Calculator Tool field contains values outside the module input.', [
          'inputBindings',
          index,
          'source',
          'fieldId',
        ]);
      }
      return;
    }
    const mediaType =
      binding.source.metaField === 'width' ||
      binding.source.metaField === 'height'
        ? 'number'
        : binding.source.metaField === 'hasTransparentPixels'
          ? 'boolean'
          : 'string';
    if (mediaType !== input.type) {
      add('Calculator media metadata type does not match the module input.', [
        'inputBindings',
        index,
        'source',
        'metaField',
      ]);
    }
    if (
      !sourceField.clientImageMetaRequirements?.fields.includes(
        binding.source.metaField
      )
    ) {
      add(
        'Calculator media input must be declared by the image field metadata requirements.',
        ['inputBindings', index, 'source', 'fieldId']
      );
    }
  });
  definition.inputs.forEach((input) => {
    if (input.required && !seenInputIds.has(input.id)) {
      add(`Calculator is missing required input ${input.id}.`, [
        'inputBindings',
      ]);
    }
  });

  const settingById = new Map(
    definition.settings.map((setting) => [setting.id, setting])
  );
  for (const [settingId, value] of Object.entries(params.instance.settings)) {
    const setting = settingById.get(settingId);
    if (!setting) {
      add('Calculator config contains an unknown module setting.', [
        'settings',
        settingId,
      ]);
      continue;
    }
    if (!isValueType(value, setting.type)) {
      add('Calculator setting type does not match the registered module.', [
        'settings',
        settingId,
      ]);
    }
    if (
      typeof value === 'number' &&
      ((setting.min !== undefined && value < setting.min) ||
        (setting.max !== undefined && value > setting.max))
    ) {
      add('Calculator setting is outside the registered module range.', [
        'settings',
        settingId,
      ]);
    }
  }
  definition.settings.forEach((setting) => {
    if (
      setting.required &&
      params.instance.settings[setting.id] === undefined
    ) {
      add(`Calculator is missing required setting ${setting.id}.`, [
        'settings',
      ]);
    }
  });

  const outputById = new Map(
    definition.outputs.map((output) => [output.id, output])
  );
  const seenOutputIds = new Set<string>();
  const seenTargetIds = new Set<string>();
  params.instance.outputMappings.forEach((mapping, index) => {
    const output = outputById.get(mapping.outputId);
    const target = fieldById.get(mapping.targetFieldId);
    if (!output) {
      add('Calculator output mapping references an unknown module output.', [
        'outputMappings',
        index,
        'outputId',
      ]);
    }
    if (seenOutputIds.has(mapping.outputId)) {
      add('Calculator output mappings must be unique by output.', [
        'outputMappings',
        index,
        'outputId',
      ]);
    }
    if (seenTargetIds.has(mapping.targetFieldId)) {
      add('Calculator output mappings must be unique by target field.', [
        'outputMappings',
        index,
        'targetFieldId',
      ]);
    }
    seenOutputIds.add(mapping.outputId);
    seenTargetIds.add(mapping.targetFieldId);
    if (!isAdminV2ParameterCalculatorVisibleParameterField(target)) {
      add('Calculator output target must be a visible parameter Tool field.', [
        'outputMappings',
        index,
        'targetFieldId',
      ]);
    } else if (
      output &&
      getAdminV2ParameterCalculatorToolFieldValueType(target!) !== output.type
    ) {
      add('Calculator output type does not match the target Tool field.', [
        'outputMappings',
        index,
        'targetFieldId',
      ]);
    }
    if (output && target) {
      const targetValues = target.options?.map((option) => option.value);
      const mappedValues = mapping.valueMap?.map((item) => item.to);
      const possibleValues = mappedValues ?? output.allowedValues;
      if (
        targetValues?.length &&
        possibleValues?.some(
          (value) =>
            !targetValues.some((targetValue) => Object.is(targetValue, value))
        )
      ) {
        add('Calculator output values are not valid target field options.', [
          'outputMappings',
          index,
          'valueMap',
        ]);
      }
      for (const item of mapping.valueMap ?? []) {
        if (
          !isValueType(item.from, output.type) ||
          getAdminV2ParameterCalculatorToolFieldValueType(target) === null ||
          !isValueType(
            item.to,
            getAdminV2ParameterCalculatorToolFieldValueType(target)!
          )
        ) {
          add('Calculator output value map types are incompatible.', [
            'outputMappings',
            index,
            'valueMap',
          ]);
          break;
        }
        if (
          output.allowedValues &&
          !output.allowedValues.some((value) => Object.is(value, item.from))
        ) {
          add(
            'Calculator output value map uses an unregistered output value.',
            ['outputMappings', index, 'valueMap']
          );
          break;
        }
      }
      if (mapping.valueMap) {
        const fromKeys = mapping.valueMap.map(
          (item) => `${typeof item.from}:${String(item.from)}`
        );
        if (new Set(fromKeys).size !== fromKeys.length) {
          add('Calculator output value map sources must be unique.', [
            'outputMappings',
            index,
            'valueMap',
          ]);
        }
        if (
          output.allowedValues?.some(
            (value) =>
              !mapping.valueMap?.some((item) => Object.is(item.from, value))
          )
        ) {
          add('Calculator output value map must cover every module value.', [
            'outputMappings',
            index,
            'valueMap',
          ]);
        }
      }
      if (
        mapping.valueMap?.some(
          (item) =>
            typeof item.to === 'number' &&
            ((target.min !== undefined && item.to < target.min) ||
              (target.max !== undefined && item.to > target.max))
        )
      ) {
        add('Calculator output value map is outside the target field range.', [
          'outputMappings',
          index,
          'valueMap',
        ]);
      }
    }
  });

  const displayItemIds = new Set(
    definition.displayItems.map((item) => item.id)
  );
  const seenDisplayIds = new Set<string>();
  params.instance.displayItemIds.forEach((itemId, index) => {
    if (!displayItemIds.has(itemId)) {
      add('Calculator preview references an unknown display item.', [
        'displayItemIds',
        index,
      ]);
    }
    if (seenDisplayIds.has(itemId)) {
      add('Calculator preview display items must be unique.', [
        'displayItemIds',
        index,
      ]);
    }
    seenDisplayIds.add(itemId);
  });

  const copyDefinitionById = new Map(
    definition.copyItems.map((item) => [item.id, item])
  );
  const seenCopyIds = new Set<string>();
  params.instance.copy.forEach((item, index) => {
    if (!copyDefinitionById.has(item.id)) {
      add('Calculator copy contains an unknown module copy item.', [
        'copy',
        index,
        'id',
      ]);
    }
    if (seenCopyIds.has(item.id)) {
      add('Calculator copy item ids must be unique.', ['copy', index, 'id']);
    }
    seenCopyIds.add(item.id);
  });
  definition.copyItems.forEach((item) => {
    if (item.required && !seenCopyIds.has(item.id)) {
      add(`Calculator is missing required copy ${item.id}.`, ['copy']);
    }
  });

  return issues;
}

export function createAdminV2ParameterCalculatorInputSource(
  input: AdminV2ParameterCalculatorDefinition['inputs'][number],
  kind: AdminV2ParameterCalculatorInputSourceKind
) {
  if (!input.allowedSources.includes(kind)) {
    throw new Error(
      `Calculator input ${input.id} does not allow source ${kind}.`
    );
  }
  if (kind === 'userInput') {
    const allowedValues =
      input.allowedValues ??
      (input.type === 'boolean' ? ([false, true] as const) : undefined);
    if (input.type !== 'number' && !allowedValues?.length) {
      throw new Error(
        `Calculator select input ${input.id} must register allowed values.`
      );
    }
    return {
      kind,
      control:
        input.type === 'number' ? ('number' as const) : ('select' as const),
      required: input.required,
      ...(allowedValues
        ? { options: allowedValues.map((value) => ({ value })) }
        : {}),
    };
  }
  if (kind === 'toolField') return { kind, fieldId: '' };
  if (kind === 'mediaMeta') {
    return {
      kind,
      fieldId: '',
      metaField:
        input.type === 'number'
          ? ('width' as const)
          : input.type === 'boolean'
            ? ('hasTransparentPixels' as const)
            : ('format' as const),
    };
  }
  if (kind === 'fixed') {
    return {
      kind,
      value:
        input.type === 'number' ? 0 : input.type === 'boolean' ? false : '',
    };
  }
  throw new Error(`Calculator input ${input.id} has no supported source.`);
}

export function createDefaultAdminV2ParameterCalculatorInstance(
  moduleId: string
) {
  const definition = getAdminV2ParameterCalculatorDefinition(moduleId);
  if (!definition) {
    throw new Error(`Unknown parameter calculator module ${moduleId}.`);
  }
  return {
    id: 'calculator-1',
    moduleId: definition.id,
    anchorFieldId: '',
    settings: Object.fromEntries(
      definition.settings.flatMap((setting) =>
        setting.defaultValue === undefined
          ? []
          : [[setting.id, setting.defaultValue]]
      )
    ),
    inputBindings: definition.inputs.map((input) => ({
      inputId: input.id,
      source: createAdminV2ParameterCalculatorInputSource(
        input,
        input.allowedSources[0]
      ),
    })),
    outputMappings: definition.outputs.map((output) => ({
      outputId: output.id,
      targetFieldId: '',
    })),
    displayItemIds: definition.displayItems.map((item) => item.id),
    copy: definition.copyItems.map((item) => ({
      id: item.id,
      text: { en: item.defaultText },
    })),
  } satisfies AdminV2ParameterCalculatorInstance;
}

export function buildAdminV2ParameterCalculatorRuntimeInstance(
  instance: AdminV2ParameterCalculatorInstance
) {
  const definition = getAdminV2ParameterCalculatorDefinition(instance.moduleId);
  if (!definition) {
    throw new Error(
      `Unknown parameter calculator module ${instance.moduleId}.`
    );
  }
  const requiredCopyIds = new Set(
    definition.copyItems.filter((item) => item.required).map((item) => item.id)
  );
  return {
    ...instance,
    copy: instance.copy.filter(
      (item) => requiredCopyIds.has(item.id) || Boolean(item.text.en?.trim())
    ),
  };
}

export function runAdminV2ParameterCalculator(params: {
  instance: Omit<AdminV2ParameterCalculatorInstance, 'copy'>;
  toolParams: Record<string, unknown>;
  userInputs: Record<string, AdminV2ParameterCalculatorScalar | undefined>;
  clientImageMeta: Record<string, unknown>;
}): AdminV2ParameterCalculatorResult {
  const definition = getAdminV2ParameterCalculatorDefinition(
    params.instance.moduleId
  );
  if (!definition) {
    return { status: 'invalid', outputs: {}, displayItems: [] };
  }
  const inputs: Record<string, AdminV2ParameterCalculatorScalar> = {};
  for (const input of definition.inputs) {
    const binding = params.instance.inputBindings.find(
      (candidate) => candidate.inputId === input.id
    );
    if (!binding) {
      return { status: 'invalid', outputs: {}, displayItems: [] };
    }
    let value: unknown;
    if (binding.source.kind === 'fixed') value = binding.source.value;
    if (binding.source.kind === 'userInput') {
      value = params.userInputs[input.id] ?? binding.source.defaultValue;
    }
    if (binding.source.kind === 'toolField') {
      value = params.toolParams[binding.source.fieldId];
    }
    if (binding.source.kind === 'mediaMeta') {
      const fieldMeta = params.clientImageMeta[binding.source.fieldId];
      if (Array.isArray(fieldMeta)) {
        return { status: 'unsupported', outputs: {}, displayItems: [] };
      }
      value =
        fieldMeta && typeof fieldMeta === 'object'
          ? (fieldMeta as Record<string, unknown>)[binding.source.metaField]
          : undefined;
    }
    if (value === undefined || value === '') {
      if (
        input.required ||
        (binding.source.kind === 'userInput' && binding.source.required)
      ) {
        return { status: 'incomplete', outputs: {}, displayItems: [] };
      }
      continue;
    }
    const parsed = adminV2ParameterCalculatorScalarSchema.safeParse(value);
    if (!parsed.success || !isValueType(parsed.data, input.type)) {
      return { status: 'invalid', outputs: {}, displayItems: [] };
    }
    if (
      input.allowedValues &&
      !input.allowedValues.some((value) => Object.is(value, parsed.data))
    ) {
      return { status: 'unsupported', outputs: {}, displayItems: [] };
    }
    if (
      binding.source.kind === 'userInput' &&
      ((typeof parsed.data === 'number' &&
        ((binding.source.min !== undefined &&
          parsed.data < binding.source.min) ||
          (binding.source.max !== undefined &&
            parsed.data > binding.source.max))) ||
        (binding.source.options?.length &&
          !binding.source.options.some((option) =>
            Object.is(option.value, parsed.data)
          )))
    ) {
      return { status: 'invalid', outputs: {}, displayItems: [] };
    }
    inputs[input.id] = parsed.data;
  }
  const result = definition.calculate({
    inputs,
    settings: params.instance.settings,
  });
  if (
    result.status === 'warning' &&
    (!result.messageId ||
      !definition.copyItems.some((item) => item.id === result.messageId))
  ) {
    return { status: 'invalid', outputs: {}, displayItems: [] };
  }
  if (result.status !== 'ready' && result.status !== 'warning') {
    return { ...result, displayItems: [] };
  }
  const outputById = new Map(
    definition.outputs.map((output) => [output.id, output])
  );
  for (const [outputId, value] of Object.entries(result.outputs)) {
    const output = outputById.get(outputId);
    if (!output || !isValueType(value, output.type)) {
      return { status: 'invalid', outputs: {}, displayItems: [] };
    }
  }
  const displayById = new Map(
    definition.displayItems.map((item) => [item.id, item])
  );
  const displayItems = params.instance.displayItemIds.flatMap((itemId) => {
    const item = displayById.get(itemId);
    const value = item ? result.outputs[item.outputId] : undefined;
    return item && value !== undefined
      ? [{ id: item.id, outputId: item.outputId, value }]
      : [];
  });
  return { ...result, displayItems };
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, stableValue(item)])
    );
  }
  return value;
}

export function createAdminV2ParameterCalculatorContextFingerprint(
  value: unknown
) {
  return JSON.stringify(stableValue(value));
}
